import type { Listing } from '@/types/listing';

/** Accepts a full listing OR an in-progress wizard draft — all field access is
 * optional-safe, so completeness works before the row is fully filled. */
type ListingLike = Partial<Listing>;

/**
 * Weighted listing-completeness model (gamification, Piotr 2026-07-13).
 * NOT "filled fields / total fields" — impact-weighted: a pet stance or a
 * fresh-renovation answer moves the bar far more than "has a fridge", and the
 * key trust/demand fields must be present to approach 100%. Pure so the edit
 * console and the "Profil Mieszkania" nudge share one source of truth.
 *
 * The 100-point bar splits 70 / 15 / 15 (Piotr 2026-07-21): CONTENT fills at
 * most 70, IDENTITY verification adds 15, OWNERSHIP verification adds 15 — so an
 * unverified listing tops out at 70% no matter how complete the content is, and
 * each verification is a visible +15% lever. Content section weights are the
 * relative units below, rescaled to sum to 70.
 *
 * Each section contributes filledWeight / totalWeight; the overall percent is
 * the weighted sum across sections. A field's `score` is 0..1 (photos and
 * amenities ramp toward a target so partial progress counts).
 */

/** Points each half of the trust bar is worth (of 100). */
const CONTENT_TARGET = 70;
const VERIFICATION_WEIGHT = 15;

/** Verification state fed in from the identity/ownership hooks (owner surfaces).
 *  Omitted → treated as not verified, so the bar caps at CONTENT_TARGET. */
export interface CompletionVerification {
  identityVerified?: boolean;
  ownershipVerified?: boolean;
}

export interface CompletionSection {
  key: string;
  /** i18n key for the section title (mirrors the edit hub / wizard steps). */
  labelKey: string;
  filledWeight: number;
  totalWeight: number;
  /** How many weighted items are still empty (drives the "2"/"1" badge). */
  missingCount: number;
  complete: boolean;
}

export interface ListingCompletion {
  /** 0..100, rounded. */
  overallPct: number;
  sections: CompletionSection[];
}

type Field = { weight: number; score: (l: ListingLike) => number };

/** score helpers */
const has = (v: unknown) => (v != null && v !== '' ? 1 : 0);
const ramp = (n: number, target: number) => Math.min(1, Math.max(0, n) / target);

const SECTIONS: Array<{ key: string; labelKey: string; fields: Field[] }> = [
  {
    key: 'address',
    labelKey: 'owner_wizard.steps.address',
    fields: [
      { weight: 3, score: (l) => has(l.address_line) },
      { weight: 3, score: (l) => has(l.city) },
      { weight: 0.5, score: (l) => has(l.postal_code) },
    ],
  },
  {
    key: 'basics',
    labelKey: 'owner_wizard.steps.basics',
    fields: [
      { weight: 3, score: (l) => has(l.rooms) },
      { weight: 3, score: (l) => has(l.area_m2) },
    ],
  },
  {
    key: 'photos',
    labelKey: 'owner_wizard.steps.photos',
    // The single biggest lever — three good photos ≈ the nudge threshold.
    fields: [{ weight: 6, score: (l) => ramp(l.photos?.length ?? 0, 3) }],
  },
  {
    key: 'details',
    labelKey: 'owner_wizard.more.title',
    fields: [
      { weight: 2, score: (l) => has(l.furnishing) },
      { weight: 1, score: (l) => has(l.building_type) },
      { weight: 1, score: (l) => has(l.building_age) },
      { weight: 1, score: (l) => has(l.kitchen_type) },
      { weight: 1, score: (l) => has(l.floor) },
      { weight: 1, score: (l) => has(l.bathrooms) },
      // Amenities: a bundle worth some weight, but each toggle barely moves it
      // (a fridge is not a renovation).
      { weight: 2, score: (l) => ramp(countAmenities(l), 4) },
    ],
  },
  {
    key: 'freshness',
    labelKey: 'owner_wizard.freshness.title',
    // The "świeżość" trio (Piotr 2026-07-14) — renovations + painting as one
    // named differentiator; each answer is a real signal, hence the weight.
    fields: [
      { weight: 2, score: (l) => has(l.kitchen_renovation) },
      { weight: 2, score: (l) => has(l.bathroom_renovation) },
      { weight: 2, score: (l) => has(l.painting_renovation) },
    ],
  },
  {
    key: 'pets',
    labelKey: 'owner_wizard.pets.title',
    // Pet-friendliness — something we believe in and that unlocks demand — is
    // heavy: NOT confirming it keeps the listing off 100%. Weight raised 3→5
    // (Piotr 2026-07-13: "sekcja zwierzęta jest mocno istotna dla nas") — the
    // second-biggest single lever after photos.
    // Decided stance = complete (an honest "Nie" is an answer); only
    // NULL/undecided leaves the +% chip on (tri-state, 2026-07-15).
    fields: [{ weight: 5, score: (l) => (l.pets_allowed != null ? 1 : 0) }],
  },
  {
    key: 'description',
    labelKey: 'owner_wizard.description.label',
    // The owner's narrative (DescriptionPipeline §3) — what only they know.
    fields: [{ weight: 3, score: (l) => has(l.description) }],
  },
  {
    key: 'fees',
    labelKey: 'owner_wizard.steps.fees',
    fields: [
      { weight: 1, score: (l) => (l.utilities_included || l.utilities_estimate_pln != null ? 1 : 0) },
      { weight: 1, score: (l) => ((l.admin_fee_includes?.length ?? 0) > 0 ? 1 : 0) },
    ],
  },
  {
    // Own step since 2026-07-14 — the hub's per-section +% badge keys on stepId.
    key: 'media',
    labelKey: 'owner_wizard.steps.media',
    fields: [{ weight: 2, score: (l) => has(l.media_settlement) }],
  },
  {
    key: 'price',
    labelKey: 'owner_wizard.steps.price',
    fields: [
      { weight: 4, score: (l) => has(l.monthly_rent_pln) },
      { weight: 1, score: (l) => has(l.deposit_pln) },
    ],
  },
];

const AMENITY_COLS: Array<keyof Listing> = [
  'has_tv',
  'has_washing_machine',
  'has_dishwasher',
  'has_fridge',
  'has_stove',
  'has_oven',
  'has_air_conditioning',
  'has_desk_home_office',
  'has_fibre_internet',
  'has_balcony',
  'has_elevator',
  'has_parking',
  'has_basement',
  'has_garden',
];

function countAmenities(l: ListingLike): number {
  return AMENITY_COLS.reduce((n, c) => n + (l[c] === true ? 1 : 0), 0);
}

/**
 * Curated CTA order for the "what to add" nudge — deliberately NOT raw
 * weight-gap. Photos and pets are strategic priorities we want surfaced first
 * (Piotr: "skoro zależy nam na info o zwierzętach, warto o tym napisać;
 * podobnie o zdjęciach") even though pets is a light-weight section, so a raw
 * gap sort (where `details` aggregates 9 fields) would bury it. Address/basics
 * sit last: they're publish-required, so if they're empty the owner is still
 * mid-wizard and the meter isn't the right place to chase them.
 */
const SUGGEST_PRIORITY = ['photos', 'identity', 'ownership', 'pets', 'freshness', 'description', 'details', 'fees', 'media', 'price', 'basics', 'address'];

/**
 * The top empty sections to prompt the owner to fill, so the meter tells them
 * *what to add* to move the bar — not just that it's at 59% (Piotr). Only
 * incomplete sections, ordered by the curated priority above.
 */
export function completionSuggestions(c: ListingCompletion, limit = 3): CompletionSection[] {
  return c.sections
    .filter((s) => !s.complete && s.totalWeight - s.filledWeight > 0)
    .sort((a, b) => SUGGEST_PRIORITY.indexOf(a.key) - SUGGEST_PRIORITY.indexOf(b.key))
    .slice(0, limit);
}

/**
 * How many points of the OVERALL bar this section can still add — the
 * homeexchange "+15%" pattern (Piotr): a reward next to each section instead
 * of (only) an amber debt counter. Ceil so any real gap reads as at least
 * "+1%" — a "+0%" chip would say "don't bother", the opposite of the nudge.
 */
export function sectionGainPct(c: ListingCompletion, s: CompletionSection): number {
  const totalW = c.sections.reduce((n, x) => n + x.totalWeight, 0);
  if (totalW <= 0) return 0;
  const gap = s.totalWeight - s.filledWeight;
  if (gap <= 0) return 0;
  return Math.ceil((gap / totalW) * 100);
}

/** Aggregate gain for a hub GROUP CARD (e.g. "Koszty najmu" = price+fees+
 * media): computed from the RAW weights across the member sections — never a
 * sum of per-section ceils (audit rule: that overstates by up to n-1 %). */
export function groupGainPct(c: ListingCompletion, keys: string[]): number {
  const totalW = c.sections.reduce((n, x) => n + x.totalWeight, 0);
  if (totalW <= 0) return 0;
  const gap = c.sections
    .filter((s) => keys.includes(s.key))
    .reduce((n, s) => n + (s.totalWeight - s.filledWeight), 0);
  if (gap <= 0) return 0;
  return Math.ceil((gap / totalW) * 100);
}

function verificationSection(
  key: string,
  labelKey: string,
  verified: boolean,
): CompletionSection {
  return {
    key,
    labelKey,
    totalWeight: VERIFICATION_WEIGHT,
    filledWeight: verified ? VERIFICATION_WEIGHT : 0,
    missingCount: verified ? 0 : 1,
    complete: verified,
  };
}

export function computeListingCompletion(
  listing: ListingLike,
  verif?: CompletionVerification,
): ListingCompletion {
  let contentTotalW = 0;
  let contentFilledW = 0;
  const contentSections = SECTIONS.map((s) => {
    let sTotal = 0;
    let sFilled = 0;
    let missing = 0;
    for (const f of s.fields) {
      const sc = f.score(listing);
      sTotal += f.weight;
      sFilled += f.weight * sc;
      if (sc < 1) missing += 1;
    }
    contentTotalW += sTotal;
    contentFilledW += sFilled;
    return {
      key: s.key,
      labelKey: s.labelKey,
      filledWeight: sFilled,
      totalWeight: sTotal,
      missingCount: missing,
      complete: missing === 0,
    };
  });

  // Rescale content so it caps at CONTENT_TARGET (70) of the 100-point bar, then
  // append the two 15-point verification sections. Total weight is always 100,
  // so the gain helpers read straight as percentages.
  const scale = contentTotalW > 0 ? CONTENT_TARGET / contentTotalW : 0;
  const identityVerified = verif?.identityVerified === true;
  const ownershipVerified = verif?.ownershipVerified === true;
  const sections: CompletionSection[] = [
    ...contentSections.map((s) => ({
      ...s,
      filledWeight: s.filledWeight * scale,
      totalWeight: s.totalWeight * scale,
    })),
    verificationSection('identity', 'id_check.title', identityVerified),
    verificationSection('ownership', 'my_apartment.ownership_label', ownershipVerified),
  ];

  const filledW =
    contentFilledW * scale +
    (identityVerified ? VERIFICATION_WEIGHT : 0) +
    (ownershipVerified ? VERIFICATION_WEIGHT : 0);
  return {
    overallPct: Math.round(filledW), // bar is exactly 100 points (70 + 15 + 15)
    sections,
  };
}
